/**
 * Mobile Petty Cash hub — KPI tiles + jump-points into Submit / List / Approvals.
 * Touch-first layout, single-column. Reuses the existing /api/petty-cash/dashboard.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Receipt, PlusCircle, ListChecks, CheckSquare, AlertTriangle, ChevronRight, Wallet,
} from 'lucide-react';
import { apiGet } from '../../lib/api';
import { formatINR } from '../../lib/format';
import { useAuth } from '../../contexts/AuthContext';

const APPROVER_ROLES = new Set(['super_admin', 'admin', 'manager', 'project_head', 'project_manager', 'site_engineer']);

export default function MobilePettyCashHome() {
  const { user } = useAuth() || {};
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const canApprove = APPROVER_ROLES.has((user?.role || '').toLowerCase());

  useEffect(() => {
    apiGet('/petty-cash/dashboard')
      .then(setKpi)
      .catch(() => setKpi(null))
      .finally(() => setLoading(false));
  }, []);

  const tiles = [
    { to: '/m/petty-cash/expense', icon: PlusCircle, title: 'Submit Expense', desc: 'New bill · GPS · approval', accent: 'from-emerald-500 to-emerald-700' },
    { to: '/m/petty-cash/list', icon: ListChecks, title: 'My Expenses', desc: 'Track status · view history', accent: 'from-indigo-500 to-indigo-700' },
    { to: '/m/cash-advance', icon: Wallet, title: 'Cash Advance', desc: 'Request · track · settle', accent: 'from-blue-500 to-blue-700' },
    ...(canApprove ? [{ to: '/m/petty-cash/approvals', icon: CheckSquare, title: 'Approvals Queue', desc: 'Review pending requests', accent: 'from-amber-500 to-amber-700' }] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-6" data-testid="m-petty-cash-home">
      <div className="bg-[#3b4872] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/m" className="p-1.5 -ml-1.5"><ArrowLeft size={20} /></Link>
        <div>
          <div className="font-display font-bold flex items-center gap-1.5"><Receipt size={14} /> Petty Cash</div>
          <div className="text-[10px] text-slate-300">Field expense control</div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* KPI strip */}
        {loading ? (
          <div className="text-center text-slate-500 text-sm py-6">Loading KPIs…</div>
        ) : kpi ? (
          <div className="grid grid-cols-2 gap-2" data-testid="m-pc-kpis">
            <Kpi label="Month Spend" value={formatINR(kpi.month_total || 0)} accent="indigo" />
            <Kpi label="Pending" value={kpi.pending_count} sub={formatINR(kpi.pending_value || 0)} accent="amber" />
            <Kpi label="Duplicate Alerts" value={kpi.duplicate_alerts || 0} accent="rose" />
            <Kpi label="High-Risk Emp." value={kpi.high_risk_employees_count || 0} accent="red" />
          </div>
        ) : null}

        {/* Quick actions */}
        <div className="space-y-2" data-testid="m-pc-tiles">
          {tiles.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              data-testid={`m-pc-tile-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className="bg-white border border-slate-200 hover:border-[#3b4872]/60 rounded-xl p-4 flex items-center gap-4 active:scale-[0.98] transition-all shadow-sm"
            >
              <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${t.accent} text-white flex items-center justify-center shrink-0`}>
                <t.icon size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm text-slate-900 leading-tight">{t.title}</div>
                <div className="text-[11px] text-slate-600 mt-0.5">{t.desc}</div>
              </div>
              <ChevronRight size={16} className="text-slate-300" />
            </Link>
          ))}
        </div>

        {/* Recent alerts */}
        {Array.isArray(kpi?.recent_alerts) && kpi.recent_alerts.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="m-pc-alerts">
            <div className="text-[10px] uppercase tracking-widest font-display text-slate-500 px-1 mb-2 flex items-center gap-1.5">
              <AlertTriangle size={11} className="text-amber-500" /> Recent Fraud Alerts
            </div>
            <div className="space-y-1.5">
              {kpi.recent_alerts.slice(0, 5).map((a, i) => (
                <div key={i} className="text-xs flex items-start gap-2">
                  <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 text-[10px] uppercase tracking-widest font-display font-bold whitespace-nowrap">{a.type?.replace(/_/g, ' ')}</span>
                  <span className="text-slate-600 truncate flex-1">{a.detail || a.message || '—'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent = 'slate' }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-display">{label}</div>
      <div className={`text-lg font-bold mt-0.5 text-${accent}-700 truncate`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}
